import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { symbolData, BLANK_SYMBOL_ID } from "../../../SymbolData";
import { Settings, GAME_LAYOUT } from "../../../settings";
import { FEATURES_CONFIG } from "../../../featuresConfig";

/**
 * FreeSpinOverlayReelController
 *
 * Manages overlay spine animations for free spin symbols.
 * Pre-positions one spine instance per symbol per position (3 × N symbols).
 * Handles landing → idle → win animation transitions.
 *
 * Stripped from main OverlayReelController:
 * - No expanding wilds
 * - No coin value text / BitmapText
 * - No bet management
 * - No sound management
 */
class FreeSpinOverlayReelController {
  constructor(
    overlaySymbolRefs,
    reelIndex,
    symbolWidth,
    symbolHeight,
    reelContainer = null
  ) {
    this.overlaySymbolRefs = overlaySymbolRefs;
    this.reelIndex = reelIndex;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelContainer = reelContainer;

    this.prePositionedSpines = new Map();
    this.activeAnimations = new Map();
    this.animationCache = new Map();

    // Animation availability cache per spineKey (same pattern as main game)
    this.animationAvailability = new Map();

    // Internal caches for performance optimization
    this._spineScaleCache = new Map();
    this._lastSymbolWidth = symbolWidth;
    this._lastSymbolHeight = symbolHeight;

    this.initializeAllSpineInstances();
  }

  getFreeSpinOverlayReelController() {
    return this;
  }

  // --- Animation availability (cached) ---

  /**
   * Combined and optimized method for checking available animations.
   * Returns object with hasLanding, hasIdle, and hasAppear using cached data.
   */
  getAvailableAnimations(symbolIndex, symbolId) {
    // Texture-only game optimization — skip landing/idle checks if disabled
    if (
      !FEATURES_CONFIG.SPINE_ANIMATIONS.LANDING_ENABLED &&
      !FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED
    ) {
      return { hasLanding: false, hasIdle: false, hasAppear: true };
    }

    // All positions share the same symbol's animations — use position 0 key for lookup
    const warmupKey = `0_${symbolId}`;
    const available = this.animationAvailability.get(warmupKey);

    if (!available) {
      return { hasLanding: false, hasIdle: false, hasAppear: false };
    }

    let hasLanding = false;
    let hasIdle = false;
    let hasAppear = false;

    for (const name of available) {
      if (!hasLanding && name.includes("land")) hasLanding = true;
      if (!hasIdle && name.includes("idle")) hasIdle = true;
      if (!hasAppear && name.includes("appear")) hasAppear = true;
      if (hasLanding && hasIdle && hasAppear) break;
    }

    // Apply feature flags to results
    return {
      hasLanding:
        FEATURES_CONFIG.SPINE_ANIMATIONS.LANDING_ENABLED && hasLanding,
      hasIdle: FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED && hasIdle,
      hasAppear,
    };
  }

  hasIdleAnimationAt(symbolIndex, symbolId) {
    const { hasIdle } = this.getAvailableAnimations(symbolIndex, symbolId);
    return hasIdle;
  }

  // --- Show animations ---

  /**
   * Show win animation using pre-positioned spine instances
   */
  showWinAnimation(symbolIndex, symbolId, animationType = "win") {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const existingAnimation = this.activeAnimations.get(symbolIndex);

    // Early return if SAME symbol and animation type already playing
    // Prevents flicker on shared symbols across multiple winning lines
    if (
      existingAnimation &&
      existingAnimation.symbolId === symbolId &&
      existingAnimation.animationType === animationType
    ) {
      spineInstance.alpha = 1;
      overlaySymbol.alpha = 1;
      return;
    }

    // Only hide if DIFFERENT symbol or animation type
    if (existingAnimation) {
      this.hideWinAnimation(symbolIndex);
    }

    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    const selectedAnimation = this.selectSpineAnimation(
      spineInstance,
      animationType,
      symbolId
    );

    if (spineInstance.state && selectedAnimation) {
      spineInstance.state.clearTracks();
      spineInstance.state.addAnimation(0, selectedAnimation, true, 0);
    }

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType,
    });
  }

  /**
   * Sync/restart win animation to frame 0 without hiding
   */
  syncWinAnimation(symbolIndex, symbolId, animationType = "win") {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    const selectedAnimation = this.selectSpineAnimation(
      spineInstance,
      animationType,
      symbolId
    );

    if (spineInstance.state && selectedAnimation) {
      const currentTrack = spineInstance.state.tracks[0];

      if (currentTrack && currentTrack.animation.name === selectedAnimation) {
        currentTrack.trackTime = 0;
      } else {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, selectedAnimation, true, 0);
      }
    }

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType,
    });
  }

  /**
   * Show landing animation using cached availability
   */
  showLandingAnimation(symbolIndex, symbolId) {
    const spineKey = `${symbolIndex}_${symbolId}`;

    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasLanding, hasIdle } = this.getAvailableAnimations(
      symbolIndex,
      symbolId
    );

    if (!hasLanding && !hasIdle) return;

    const existingAnimation = this.activeAnimations.get(symbolIndex);
    if (existingAnimation && existingAnimation.animationType === "win") {
      this.hideWinAnimation(symbolIndex);
    }

    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    if (hasLanding && hasIdle) {
      const landingAnimation = this.selectSpineAnimation(
        spineInstance,
        "land",
        symbolId
      );
      const idleAnimation = this.selectSpineAnimation(
        spineInstance,
        "idle",
        symbolId
      );

      if (spineInstance.state && landingAnimation && idleAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, landingAnimation, false, 0);
        spineInstance.state.addAnimation(0, idleAnimation, true, 0);
      }
    } else if (hasIdle) {
      const idleAnimation = this.selectSpineAnimation(
        spineInstance,
        "idle",
        symbolId
      );
      if (spineInstance.state && idleAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, idleAnimation, true, 0);
      }
    } else if (hasLanding) {
      const landingAnimation = this.selectSpineAnimation(
        spineInstance,
        "land",
        symbolId
      );
      if (spineInstance.state && landingAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, landingAnimation, true, 0);
      }
    }

    const recordedType =
      hasLanding && hasIdle ? "idle" : hasIdle ? "idle" : "landing";

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: recordedType,
      hadLandingAnimation: hasLanding,
    });
  }

  /**
   * Show idle animation
   */
  showIdleAnimation(symbolIndex, symbolId) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasIdle } = this.getAvailableAnimations(symbolIndex, symbolId);
    if (!hasIdle) return;

    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    const idleAnimation = this.selectSpineAnimation(
      spineInstance,
      "idle",
      symbolId
    );
    if (spineInstance.state && idleAnimation) {
      spineInstance.state.clearTracks();
      spineInstance.state.addAnimation(0, idleAnimation, true, 0);
    }

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "idle",
    });
  }

  /**
   * Smoothly crossfade from the current animation (e.g. win) to idle.
   * Uses setAnimation with a mix duration so Spine blends poses — no snap.
   */
  transitionToIdle(symbolIndex, symbolId) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasIdle } = this.getAvailableAnimations(symbolIndex, symbolId);
    if (!hasIdle) return;

    // Ensure visible and updating
    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    const idleAnimation = this.selectSpineAnimation(
      spineInstance,
      "idle",
      symbolId
    );
    if (spineInstance.state && idleAnimation) {
      // Don't clearTracks — let Spine blend from the current pose
      const entry = spineInstance.state.setAnimation(0, idleAnimation, true);
      if (entry) {
        entry.mixDuration = 0.3;
      }
    }

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "idle",
    });
  }

  // --- Hide animations ---

  hideWinAnimation(symbolIndex) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    const animationData = this.activeAnimations.get(symbolIndex);
    if (!animationData || !overlaySymbol) return;

    const { spineInstance } = animationData;
    spineInstance.alpha = 0.001;
    spineInstance.autoUpdate = false;
    overlaySymbol.alpha = 0.001;

    // Full skeleton reset on hide — safe because spine is invisible
    if (spineInstance.state) {
      spineInstance.state.clearTracks();
    }
    if (spineInstance.skeleton?.setToSetupPose) {
      spineInstance.skeleton.setToSetupPose();
    }

    this.activeAnimations.delete(symbolIndex);
  }

  hideLandingAnimation(symbolIndex) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    const animationData = this.activeAnimations.get(symbolIndex);
    if (!animationData || !overlaySymbol) return;

    if (animationData.animationType !== "landing") return;

    const { spineInstance } = animationData;
    spineInstance.alpha = 0.001;
    spineInstance.autoUpdate = false;
    overlaySymbol.alpha = 0.001;

    if (spineInstance.state) {
      spineInstance.state.clearTracks();
    }
    if (spineInstance.skeleton?.setToSetupPose) {
      spineInstance.skeleton.setToSetupPose();
    }

    this.activeAnimations.delete(symbolIndex);
  }

  hideAllAnimations(exceptPositions = null) {
    // First: hide tracked animations
    const activeIndexes = Array.from(this.activeAnimations.keys());
    activeIndexes.forEach((symbolIndex) => {
      if (exceptPositions && exceptPositions.has(symbolIndex)) {
        return;
      }
      this.hideWinAnimation(symbolIndex);
    });

    // Full sweep of ALL pre-positioned spines to catch orphaned autoUpdate=true
    if (!exceptPositions) {
      for (const spine of this.prePositionedSpines.values()) {
        if (spine && spine.autoUpdate) {
          spine.autoUpdate = false;
          spine.alpha = 0.001;
          if (spine.state) {
            spine.state.clearTracks();
          }
        }
      }
      this.activeAnimations.clear();
    }
  }

  /**
   * Hide a single symbol's overlay animation at a specific row.
   * @param {number} row - Row index (0, 1, 2)
   */
  hideAnimationAt(row) {
    // Hide tracked win animation
    if (this.activeAnimations.has(row)) {
      this.hideWinAnimation(row);
    }
    // Also kill any pre-positioned spine at this row
    const spine = this.prePositionedSpines.get(row);
    if (spine && spine.autoUpdate) {
      spine.autoUpdate = false;
      spine.alpha = 0.001;
      if (spine.state) {
        spine.state.clearTracks();
      }
    }
  }

  hideAllAnimationsExcept(upcomingPositions = []) {
    if (!upcomingPositions || upcomingPositions.length === 0) {
      this.hideAllAnimations();
      return;
    }

    // Skip if no animations are active on this reel
    if (this.activeAnimations.size === 0) return;

    const preserveMap = new Map();
    for (let i = 0; i < upcomingPositions.length; i++) {
      const [reelIndex, symbolIndex, symbolId] = upcomingPositions[i];
      if (reelIndex === this.reelIndex) {
        preserveMap.set(symbolIndex, symbolId);
      }
    }

    for (const [symbolIndex, animationData] of this.activeAnimations) {
      const preservedSymbolId = preserveMap.get(symbolIndex);
      if (preservedSymbolId !== animationData.symbolId) {
        this.hideWinAnimation(symbolIndex);
      }
    }
  }

  /**
   * Hide all landing animations (called when winning lines start)
   */
  hideAllLandingAnimations() {
    const activeIndexes = Array.from(this.activeAnimations.keys());

    activeIndexes.forEach((symbolIndex) => {
      const animationData = this.activeAnimations.get(symbolIndex);
      if (animationData && animationData.animationType === "landing") {
        this.hideWinAnimation(symbolIndex);
      }
    });
  }

  // --- Entry points for symbol reveal ---

  handleReelStop(reelData) {
    if (!reelData || reelData.length === 0) return;

    for (let position = 0; position < GAME_LAYOUT.SYMBOLS_VISIBLE; position++) {
      const symbolId = reelData[position];
      if (symbolId !== undefined) {
        this.showLandingAnimation(position, symbolId);
      }
    }
  }

  handleSymbolReveal(symbolIndex, symbolId) {
    if (symbolId === undefined) return;
    this.showLandingAnimation(symbolIndex, symbolId);
  }

  // --- Spine scale and positioning ---

  calculateSpineScale(
    spineInstance,
    containerWidth,
    containerHeight,
    skipAnimationSetup = false
  ) {
    if (
      !skipAnimationSetup &&
      spineInstance &&
      spineInstance.state &&
      spineInstance.state.data &&
      spineInstance.state.data.skeletonData.animations.length > 0
    ) {
      const defaultAnimation =
        spineInstance.state.data.skeletonData.animations[0];
      spineInstance.state.addAnimation(0, defaultAnimation.name, true, 0);
    }

    // Check cache first
    const cacheKey = `${containerWidth}_${containerHeight}`;
    if (this._spineScaleCache.has(cacheKey)) {
      return this._spineScaleCache.get(cacheKey);
    }

    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;
    const heightScale = containerHeight / nativeHeight;
    const scale = heightScale;

    const scaledWidth = nativeWidth * scale;
    const scaledHeight = nativeHeight * scale;
    const x = (containerWidth - scaledWidth) / 2;
    const y = (containerHeight - scaledHeight) / 2;

    const result = {
      scale,
      nativeWidth,
      nativeHeight,
      x,
      y,
      scaledWidth,
      scaledHeight,
    };

    // Cache result
    this._spineScaleCache.set(cacheKey, result);

    return result;
  }

  resizeAnimations(newSymbolWidth, newSymbolHeight) {
    if (
      this.symbolWidth === newSymbolWidth &&
      this.symbolHeight === newSymbolHeight
    ) {
      return;
    }

    this.symbolWidth = newSymbolWidth;
    this.symbolHeight = newSymbolHeight;

    // Invalidate caches when dimensions change
    this._spineScaleCache.clear();
    this._lastSymbolWidth = newSymbolWidth;
    this._lastSymbolHeight = newSymbolHeight;

    const nativeHeight = Settings.SYMBOL_HEIGHT;
    const heightScale = newSymbolHeight / nativeHeight;
    const scaleRatio = heightScale;

    for (const spineInstance of this.prePositionedSpines.values()) {
      if (spineInstance?.scale) {
        spineInstance.scale.set(scaleRatio);
      }
    }

    for (let i = 0; i < this.overlaySymbolRefs.length; i++) {
      const overlaySymbol = this.overlaySymbolRefs[i];
      if (overlaySymbol) {
        overlaySymbol.y = newSymbolHeight * i;
      }
    }
  }

  // --- Spine creation ---

  createSpineInstance(symbolId) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo?.spine || symbolInfo.spine === "") return null;

    const spineName = symbolInfo.spine;
    const skeletonAlias = `${spineName}-skeleton`;
    const atlasAlias = `${spineName}-atlas`;
    const assets = PIXI.Assets.cache;

    if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

    try {
      return Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
        autoUpdate: false,
      });
    } catch {
      return null;
    }
  }

  // --- Animation selection ---

  selectSpineAnimation(spineInstance, animationType, symbolId) {
    const cacheKey = `${symbolId}_${animationType}`;
    let cachedAnimation = this.animationCache.get(cacheKey);
    if (cachedAnimation !== undefined) return cachedAnimation;

    if (!spineInstance.skeleton?.data?.animations) {
      this.animationCache.set(cacheKey, null);
      return null;
    }

    const animations = spineInstance.skeleton.data.animations;
    let selectedAnimation = null;

    if (animationType === "auto") {
      selectedAnimation = animations[0]?.name || null;
    } else {
      const exactMatch = animations.find((anim) =>
        anim.name.toLowerCase().includes(animationType.toLowerCase())
      );
      selectedAnimation = exactMatch
        ? exactMatch.name
        : animations[0]?.name || null;
    }

    this.animationCache.set(cacheKey, selectedAnimation);
    return selectedAnimation;
  }

  _smoothTransitionToAnimation(
    spineInstance,
    newAnimationType,
    symbolId,
    loop = true
  ) {
    if (!spineInstance || !spineInstance.state) return false;

    const newAnimation = this.selectSpineAnimation(
      spineInstance,
      newAnimationType,
      symbolId
    );
    if (!newAnimation) return false;

    try {
      const currentTracks = spineInstance.state.tracks;
      const hasCurrentAnimation =
        currentTracks && currentTracks.length > 0 && currentTracks[0];

      if (hasCurrentAnimation) {
        const currentTrack = currentTracks[0];
        const currentAnimationName = currentTrack.animation
          ? currentTrack.animation.name
          : null;

        if (currentAnimationName === newAnimation) return true;
        spineInstance.state.addAnimation(0, newAnimation, loop, 0.1);
      } else {
        spineInstance.state.setAnimation(0, newAnimation, loop);
      }
      return true;
    } catch {
      return false;
    }
  }

  // --- Spine initialization ---

  async initializeAllSpineInstances() {
    this.createAllPrePositionedSpines();
  }

  createAllPrePositionedSpines() {
    const WARMUP_POSITION = 0;
    const WARMUP_REEL = 0;

    for (
      let symbolIndex = 0;
      symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
      symbolIndex++
    ) {
      const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
      if (!overlaySymbol) continue;

      for (
        let symbolId = 0;
        symbolId < Object.keys(symbolData).length;
        symbolId++
      ) {
        // Skip blank symbols when Hold and Win is enabled
        const shouldExcludeBlank =
          FEATURES_CONFIG.HOLD_AND_WIN?.ENABLED && symbolId === BLANK_SYMBOL_ID;

        if (shouldExcludeBlank) continue;

        const symbolInfo = symbolData[symbolId];
        // Skip if symbol doesn't exist or has no valid spine
        if (
          !symbolInfo ||
          !symbolInfo.spine ||
          symbolInfo.spine === null ||
          symbolInfo.spine === "" ||
          typeof symbolInfo.spine !== "string"
        ) {
          continue;
        }

        let spineInstance;

        // First reel, first position — create, warm up, and cache animation availability
        if (
          this.reelIndex === WARMUP_REEL &&
          symbolIndex === WARMUP_POSITION
        ) {
          spineInstance = this.createSpineInstance(symbolId);
          if (!spineInstance) continue;

          const scaleData = this.calculateSpineScale(
            spineInstance,
            this.symbolWidth,
            this.symbolHeight,
            true
          );

          spineInstance.position.set(scaleData.x, scaleData.y);
          spineInstance.scale.set(scaleData.scale);
          spineInstance.zIndex = 10;
          spineInstance.alpha = 0.001;
          overlaySymbol.addChild(spineInstance);

          // Build animation availability cache from warmed-up spine
          const key = `${symbolIndex}_${symbolId}`;
          if (spineInstance.skeleton?.data?.animations?.length > 0) {
            const available = new Set(
              spineInstance.skeleton.data.animations.map((a) =>
                a.name.toLowerCase()
              )
            );
            if (available.size > 0) {
              this.animationAvailability.set(key, available);
            }
          }

          // Warm up animations (shader compilation + GPU buffer upload)
          // "win" is warmed LAST so its meshes survive after cleanup
          this.warmUpSpineAnimation(spineInstance, symbolId, "appear");
          this.warmUpSpineAnimation(spineInstance, symbolId, "idle");
          this.warmUpSpineAnimation(spineInstance, symbolId, "land");
          this.warmUpSpineAnimation(spineInstance, symbolId, "win");

          this.scheduleWarmupCleanup(spineInstance);

          const spineKey = `${symbolIndex}_${symbolId}`;
          this.prePositionedSpines.set(spineKey, spineInstance);
        } else {
          // All other reels/positions — shaders already compiled
          spineInstance = this.createSpineInstance(symbolId);
          if (!spineInstance) continue;

          const scaleData = this.calculateSpineScale(
            spineInstance,
            this.symbolWidth,
            this.symbolHeight,
            true
          );

          spineInstance.position.set(scaleData.x, scaleData.y);
          spineInstance.scale.set(scaleData.scale);
          spineInstance.zIndex = 10;
          spineInstance.alpha = 0.001;
          overlaySymbol.addChild(spineInstance);

          // Build animation availability cache (use position 0 key for lookup)
          const warmupKey = `${WARMUP_POSITION}_${symbolId}`;
          let available = this.animationAvailability.get(warmupKey);

          if (
            !available &&
            spineInstance.skeleton?.data?.animations?.length > 0
          ) {
            available = new Set(
              spineInstance.skeleton.data.animations.map((a) =>
                a.name.toLowerCase()
              )
            );
            if (available.size > 0) {
              this.animationAvailability.set(warmupKey, available);
            }
          }

          // Warm up GPU mesh buffers
          this.warmUpSpineAnimation(spineInstance, symbolId, "appear");
          this.warmUpSpineAnimation(spineInstance, symbolId, "idle");
          this.warmUpSpineAnimation(spineInstance, symbolId, "land");
          this.warmUpSpineAnimation(spineInstance, symbolId, "win");
          this.scheduleWarmupCleanup(spineInstance);

          const spineKey = `${symbolIndex}_${symbolId}`;
          this.prePositionedSpines.set(spineKey, spineInstance);
        }
      }
    }
  }

  warmUpSpineAnimation(spineInstance, symbolId, animationType = "win") {
    if (!spineInstance?.state) return;
    try {
      const animation = this.selectSpineAnimation(
        spineInstance,
        animationType,
        symbolId
      );
      if (!animation) return;

      // Use setAnimation (replaces current) — preserves previous mesh data
      // until new one replaces it, giving GPU time to render each mesh
      spineInstance.state.setAnimation(0, animation, false);
      spineInstance.state.update(0.016);
      spineInstance.skeleton.updateWorldTransform();

      spineInstance.alpha = 0.001;
      const overlaySymbol = spineInstance.parent;
      if (overlaySymbol) overlaySymbol.alpha = 0.001;
    } catch {}
  }

  /**
   * Finalize warmup cleanup after GPU has had time to render all animation meshes.
   * 3-frame RAF ensures PIXI's renderer has drawn and uploaded all vertex/texture data.
   */
  scheduleWarmupCleanup(spineInstance) {
    if (!spineInstance?.state) return;

    const overlaySymbol = spineInstance.parent;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (spineInstance.state) {
            spineInstance.state.clearTracks();
            spineInstance.autoUpdate = false;
          }
          spineInstance.alpha = 0.001;
          if (overlaySymbol) overlaySymbol.alpha = 0.001;
        });
      });
    });
  }

  // --- Deep cleanup ---

  forceDeepSpineCleanup() {
    for (const spineInstance of this.prePositionedSpines.values()) {
      if (spineInstance?.state) {
        spineInstance.state.clearTracks();
        spineInstance.autoUpdate = false;
        spineInstance.alpha = 0.001;
      }
    }

    for (let i = 0; i < this.overlaySymbolRefs.length; i++) {
      const overlaySymbol = this.overlaySymbolRefs[i];
      if (overlaySymbol?.alpha > 0) {
        overlaySymbol.alpha = 0.001;
      }
    }

    this.activeAnimations.clear();
  }

  // --- Cleanup ---

  cleanup() {
    this.hideAllAnimations();

    for (const spineInstance of this.prePositionedSpines.values()) {
      if (spineInstance) {
        if (spineInstance.state) spineInstance.state.clearTracks();
        if (spineInstance.parent) {
          spineInstance.parent.removeChild(spineInstance);
        }
        spineInstance.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      }
    }

    this.prePositionedSpines.clear();
    this.activeAnimations.clear();
    this.animationCache.clear();
    this.animationAvailability.clear();
  }
}

export default FreeSpinOverlayReelController;
